/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: AssetsDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/2/10 @ 187
 * ---
 * Description:
 */

import {observer} from 'mobx-react';
import {Button, Callout, Classes, Dialog, Intent, NonIdealState, Spinner} from '@blueprintjs/core';
import classNames from 'classnames';
import {currentThemeIsDark, dataURLFromBase64, getAssetNameByBucketUrl} from '../../../common/utils';
import {useCallback, useEffect, useState} from 'react';
import {MLEImage} from '../../../entities';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {mleDomainGqlClient} from '../../../api/domain';
import {GET_IMAGES_BY_COMPANY_ID} from '../../../api/domain/queries.gql';
import {AssetThumbnail} from './AssetThumbnail';
import {UPLOAD_IMAGE} from '../../../api/domain/mutations.gql';
import {useMutation} from '@apollo/client';
import {ShapeTypes} from '../../../common/enums';
import {MleToaster, Toasts} from '../../../common/toast';
import {BaseDialogProps, HasEditor} from '../../../common/props';
import {AddImageParams} from '../../../store/features/shape/precompiled/feature/ShapesFeature';

interface Props extends HasEditor, BaseDialogProps {
}

function selectFile(contentType: string, multiple: boolean): Promise<File | File[]> {
    return new Promise(resolve => {
        let input = document.createElement('input');
        input.type = 'file';
        input.multiple = multiple;
        input.accept = contentType;

        input.onchange = () => {
            let files: File[] = Array.from(input.files as any);
            if (multiple)
                resolve(files);
            else
                resolve(files[0]);
        };

        input.click();
    });
}

export const AssetsDialog = observer((props: Props) => {
    const [assets, setAssets] = useState<MLEImage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const authData = useSelector((state: RootState) => state.authState);

    const [uploadImage] = useMutation(UPLOAD_IMAGE,
        {client: mleDomainGqlClient});

    const handleFileUpload = useCallback((file: File) => {
        (async () => {
            try {
                setIsLoading(true);
                await uploadImage({
                    variables: {
                        file
                    }
                });

                const {data} = await mleDomainGqlClient.query({
                    query: GET_IMAGES_BY_COMPANY_ID,
                    variables: {id: authData.user?.company?.id,},
                    fetchPolicy: 'no-cache'
                });

                // Update images
                setAssets(data.company.images);
                setIsLoading(false);
            } catch (e) {
                MleToaster.getInstance().show(Toasts.GENERIC_ERROR);
            }
        })();
    }, [setAssets, setIsLoading, uploadImage, authData.user.id]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            if (authData.user.id) {
                const images = await mleDomainGqlClient.query({
                    query: GET_IMAGES_BY_COMPANY_ID,
                    variables: {
                        id: authData.user?.company?.id
                    },
                    fetchPolicy: 'no-cache'
                });
                setAssets(images.data.company?.images ?? []);
                setIsLoading(false);
            }
        })();
    }, [setAssets, props.isOpen, authData.user.id, setIsLoading]);

    const handleImageRemove = useCallback((id) => {
        setIsLoading(true);
        setAssets(assets.filter(it => it.id !== id));
        setIsLoading(false);
    }, [setAssets, assets]);

    const handleAddToLabel = useCallback(async (id: number) => {
        const asset = assets.filter(it => it.id === id)[0];

        await props.editor.shapesFeature.createShape(ShapeTypes.IMAGE, {
            src: asset.bucketURL,
            dataurl: dataURLFromBase64(asset.content),
            assetId: asset.id
        } as AddImageParams);
        props.onClose();
    }, [assets]);

    return (
        <Dialog
            usePortal
            className={classNames({'bp4-dark': currentThemeIsDark()})}
            style={{width: '50rem'}}
            isOpen={props.isOpen}
            title="Libreria immagini"
            onClose={props.onClose}
            canOutsideClickClose={false}
        >
            <div className={Classes.DIALOG_BODY}>
                <Callout
                    intent={Intent.PRIMARY}
                    className="mb-3"
                >
                    La libreria è condivisa tra tutti i progetti e i membri
                    di <strong>{authData.user?.company?.name}</strong>
                </Callout>
                <div className="bp4-text-muted mb-2">Per aggiungere un&apos;immagine al
                    piano di lavoro, cliccala due volte o usa l'icona apposita
                </div>
                <div className="flex flex-col">
                    <div className="relative flex flex-col h-96 overflow-y-auto">
                        <div>
                            {
                                !isLoading && assets.length > 0 &&
                                <div className="overflow-y-auto py-2">
                                    <div
                                        className="grid grid-cols-3 gap-3 sm:grid-cols-2 text-white"> {
                                        assets.map(it => <AssetThumbnail
                                                onDelete={handleImageRemove}
                                                id={it.id}
                                                src={it.bucketURL}
                                                name={getAssetNameByBucketUrl(it.bucketURL)}
                                                onAddAsset={handleAddToLabel}
                                            />
                                        )}
                                    </div>
                                </div>}
                            {
                                !isLoading && assets.length == 0 &&
                                <div
                                    className="flex justify-center flex-col align-middle my-auto mt-10">
                                    <NonIdealState
                                        icon="upload"
                                        title="Nessuna immagine caricata"
                                        description="Carica un'immagine"
                                    />
                                </div>
                            }
                            {
                                isLoading &&
                                <div className="flex flex-col justify-center m-3"><Spinner
                                    size={20}/></div>
                            }
                        </div>
                    </div>
                    <div className="mt-auto flex flex-row justify-end -mb-4">
                        <Button icon="upload" intent={Intent.PRIMARY}
                                onClick={async () => {
                                    const f = await selectFile("image/jpeg,image/png,image/jpg", false)
                                    handleFileUpload(f as File);
                                }}>
                            Carica nuova immagine
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
});
